/**
 * The partner portal ships pt-BR only (see CLAUDE.md). The structure is the same as bodyprint-web,
 * so a locale is added by listing it here and adding its catalogue in `translations/index.ts`.
 */
const SUPPORTED_LOCALES = ['pt-BR'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** Locale used as the reference resource and as the last fallback. */
export const DEFAULT_LOCALE: Locale = 'pt-BR';

/**
 * Matches a BCP 47 tag against the supported list: exact match first, then the
 * language subtag (`pt-PT` → `pt-BR`).
 */
export function matchSupportedLocale(tag: string | null | undefined): Locale | null {
  if (!tag) return null;

  const normalized = tag.replace('_', '-');
  const exact = SUPPORTED_LOCALES.find(
    (locale) => locale.toLowerCase() === normalized.toLowerCase(),
  );
  if (exact) return exact;

  const language = normalized.split('-')[0]?.toLowerCase();
  if (!language) return null;

  return (
    SUPPORTED_LOCALES.find((locale) => locale.split('-')[0].toLowerCase() === language) ?? null
  );
}

/**
 * Reads the browser language, honouring the user's ordered preference list.
 * Falls back to the default locale when `navigator` is unavailable, as during
 * a non-browser render.
 */
export function resolveDeviceLocale(): Locale {
  try {
    for (const tag of navigator.languages ?? []) {
      const matched = matchSupportedLocale(tag);
      if (matched) return matched;
    }

    return matchSupportedLocale(navigator.language) ?? DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}
